import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Target, Save, ChevronLeft, ChevronRight, DollarSign, Users, Building2, AlertCircle, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useUserSalesGoals, useSalesConsultants } from '@/hooks/useUserSalesGoals';
import { useSalesGoals, useConsolidatedSalesGoal } from '@/hooks/useSalesGoals';
import { useGlobalSourceWeights, useUpsertGlobalSourceWeights, useSourceGoals, useSourceActuals, getAvailableSources } from '@/hooks/useUserSalesGoalsBySource';
import { toast } from 'sonner';
import { format, startOfMonth, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type BusinessUnitTab = 'consolidated' | 'us' | 'br';

interface GlobalWeightRow {
  source: string;
  weightPct: number;
}

/**
 * Global Source Weights editor — lives in the Team Goal card
 */
function GlobalSourceWeightsEditor({
  year,
  month,
  businessUnit,
  teamGoalAmount,
  avgTicket,
}: {
  year: number;
  month: number;
  businessUnit: string | null;
  teamGoalAmount: number;
  avgTicket: number;
}) {
  const { data: globalWeights, isLoading } = useGlobalSourceWeights(year, month, businessUnit);
  const upsertMutation = useUpsertGlobalSourceWeights(year, month, businessUnit);

  const buildInitialRows = useCallback((): GlobalWeightRow[] => {
    if (!globalWeights || globalWeights.length === 0) return [];
    return globalWeights.map(w => ({
      source: w.source,
      weightPct: w.weight_pct,
    }));
  }, [globalWeights]);

  const [rows, setRows] = useState<GlobalWeightRow[]>([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  React.useEffect(() => {
    if (!hasLocalChanges) {
      setRows(buildInitialRows());
    }
  }, [globalWeights, buildInitialRows, hasLocalChanges]);

  const handleWeightChange = (index: number, value: string) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], weightPct: parseFloat(value) || 0 };
    setRows(newRows);
    setHasLocalChanges(true);
  };

  const handleAddSource = (source: string) => {
    if (rows.some(r => r.source === source)) return;
    setRows([...rows, { source, weightPct: 0 }]);
    setHasLocalChanges(true);
  };

  const handleRemoveSource = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
    setHasLocalChanges(true);
  };

  const handleSave = () => {
    upsertMutation.mutate(
      rows.filter(r => r.weightPct > 0).map(r => ({ source: r.source, weightPct: r.weightPct })),
      {
        onSuccess: () => {
          toast.success('Pesos por source salvos');
          setHasLocalChanges(false);
        },
        onError: () => toast.error('Erro ao salvar pesos'),
      }
    );
  };

  const totalWeight = rows.reduce((s, r) => s + r.weightPct, 0);
  const weightValid = Math.abs(totalWeight - 100) < 0.01 || totalWeight === 0;
  const availableSources = getAvailableSources().filter(s => !rows.some(r => r.source === s));

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  if (isLoading) return <Skeleton className="h-20 w-full mt-4" />;

  return (
    <div className="mt-4 rounded-lg border border-border bg-muted/30 overflow-hidden">
      <div className="px-3 py-2 bg-muted/50 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground">📊 Distribuição por Source (Global — aplica a todos os consultores)</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs w-[180px]">Source</TableHead>
            <TableHead className="text-xs w-[80px] text-center">%</TableHead>
            <TableHead className="text-xs w-[110px] text-right">Meta $</TableHead>
            <TableHead className="text-xs w-[70px] text-center">Meta QTD</TableHead>
            <TableHead className="text-xs w-[40px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            const goalAmt = (row.weightPct / 100) * teamGoalAmount;
            const goalDeals = avgTicket > 0 ? Math.round(goalAmt / avgTicket) : 0;
            return (
              <TableRow key={row.source} className="hover:bg-muted/20">
                <TableCell className="text-xs font-medium">{row.source}</TableCell>
                <TableCell className="text-center">
                  <Input
                    type="number"
                    value={row.weightPct || ''}
                    onChange={(e) => handleWeightChange(index, e.target.value)}
                    className="w-16 h-7 text-xs text-center mx-auto"
                    min={0}
                    max={100}
                    placeholder="0"
                  />
                </TableCell>
                <TableCell className="text-xs text-right font-medium">{formatCurrency(goalAmt)}</TableCell>
                <TableCell className="text-xs text-center">{goalDeals}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleRemoveSource(index)}>
                    <Trash2 className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length > 0 && (
            <TableRow className={`font-bold border-t-2 ${!weightValid ? 'bg-destructive/10' : 'bg-muted/50'}`}>
              <TableCell className="text-xs font-bold">TOTAL</TableCell>
              <TableCell className={`text-xs text-center font-bold ${!weightValid ? 'text-destructive' : ''}`}>
                {totalWeight.toFixed(0)}%
              </TableCell>
              <TableCell className="text-xs text-right font-bold">{formatCurrency((totalWeight / 100) * teamGoalAmount)}</TableCell>
              <TableCell className="text-xs text-center font-bold">
                {avgTicket > 0 ? Math.round(((totalWeight / 100) * teamGoalAmount) / avgTicket) : 0}
              </TableCell>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between p-3 border-t border-border">
        <div className="flex items-center gap-2">
          {availableSources.length > 0 && (
            <Select onValueChange={handleAddSource}>
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue placeholder="Adicionar source..." />
              </SelectTrigger>
              <SelectContent>
                {availableSources.map(s => (
                  <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!weightValid && totalWeight > 0 && (
            <span className="text-xs text-destructive">Soma dos pesos deve ser 100%</span>
          )}
        </div>
        {hasLocalChanges && (
          <Button size="sm" onClick={handleSave} disabled={upsertMutation.isPending} className="h-7 text-xs">
            <Save className="h-3 w-3 mr-1" />
            Salvar Pesos
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only source breakdown for a consultant, using global weights × individual goal
 */
function ConsultantSourceReadonly({
  globalWeights,
  userTotalGoal,
  avgTicket,
  actualsMap,
  userId,
}: {
  globalWeights: Array<{ source: string; weight_pct: number }>;
  userTotalGoal: number;
  avgTicket: number;
  actualsMap: Map<string, any[]>;
  userId: string;
}) {
  const actuals = actualsMap?.get(userId) || [];

  if (!globalWeights || globalWeights.length === 0) {
    return (
      <div className="px-4 pb-4">
        <p className="text-xs text-muted-foreground italic">Nenhum peso global configurado. Configure na seção "Meta Total da Equipe".</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const rows = globalWeights.map(w => {
    const goalAmt = (w.weight_pct / 100) * userTotalGoal;
    const goalDeals = avgTicket > 0 ? Math.round(goalAmt / avgTicket) : 0;
    const actual = actuals.find((a: any) => a.source === w.source);
    const actualRevenue = actual?.revenue || 0;
    const actualDeals = actual?.deal_count || 0;
    const achievement = goalAmt > 0 ? (actualRevenue / goalAmt) * 100 : 0;
    return { source: w.source, weightPct: w.weight_pct, goalAmt, goalDeals, actualRevenue, actualDeals, achievement };
  });

  const totalGoalAmt = rows.reduce((s, r) => s + r.goalAmt, 0);
  const totalGoalDeals = rows.reduce((s, r) => s + r.goalDeals, 0);
  const totalActRev = rows.reduce((s, r) => s + r.actualRevenue, 0);
  const totalActDeals = rows.reduce((s, r) => s + r.actualDeals, 0);

  return (
    <div className="px-4 pb-4">
      <div className="rounded-lg border border-border bg-muted/30 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs w-[180px]">Source</TableHead>
              <TableHead className="text-xs w-[60px] text-center">%</TableHead>
              <TableHead className="text-xs w-[110px] text-right">Meta $</TableHead>
              <TableHead className="text-xs w-[70px] text-center">Meta QTD</TableHead>
              <TableHead className="text-xs w-[110px] text-right">Real. $</TableHead>
              <TableHead className="text-xs w-[70px] text-center">Real. QTD</TableHead>
              <TableHead className="text-xs w-[80px] text-right">% Ating.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.source} className="hover:bg-muted/20">
                <TableCell className="text-xs font-medium">{r.source}</TableCell>
                <TableCell className="text-xs text-center text-muted-foreground">{r.weightPct}%</TableCell>
                <TableCell className="text-xs text-right font-medium">{formatCurrency(r.goalAmt)}</TableCell>
                <TableCell className="text-xs text-center">{r.goalDeals}</TableCell>
                <TableCell className="text-xs text-right">{formatCurrency(r.actualRevenue)}</TableCell>
                <TableCell className="text-xs text-center">{r.actualDeals}</TableCell>
                <TableCell className={`text-xs text-right font-medium ${r.achievement >= 100 ? 'text-green-600' : r.achievement >= 50 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  {r.goalAmt > 0 ? `${r.achievement.toFixed(1)}%` : '-'}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="font-bold bg-muted/50 border-t-2">
              <TableCell className="text-xs font-bold">TOTAL</TableCell>
              <TableCell className="text-xs text-center font-bold">-</TableCell>
              <TableCell className="text-xs text-right font-bold">{formatCurrency(totalGoalAmt)}</TableCell>
              <TableCell className="text-xs text-center font-bold">{totalGoalDeals}</TableCell>
              <TableCell className="text-xs text-right font-bold">{formatCurrency(totalActRev)}</TableCell>
              <TableCell className="text-xs text-center font-bold">{totalActDeals}</TableCell>
              <TableCell className="text-xs text-right font-bold">
                {totalGoalAmt > 0 ? `${((totalActRev / totalGoalAmt) * 100).toFixed(1)}%` : '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="px-3 py-2 border-t border-border">
          <span className="text-xs text-muted-foreground italic">Pesos herdados da configuração global. Para alterar, edite na seção "Meta Total da Equipe".</span>
        </div>
      </div>
    </div>
  );
}

export default function SalesGoalsSettings() {
  const [selectedDate, setSelectedDate] = useState(startOfMonth(new Date()));
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth() + 1;

  const [activeTab, setActiveTab] = useState<BusinessUnitTab>('consolidated');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const getBusinessUnitValue = (tab: BusinessUnitTab): string | null => {
    if (tab === 'us') return 'us';
    if (tab === 'br') return 'br';
    return null;
  };

  const businessUnitValue = getBusinessUnitValue(activeTab);

  const [editedGoals, setEditedGoals] = useState<Record<string, string>>({});
  const [editedTeamGoal, setEditedTeamGoal] = useState<string | null>(null);
  const [editedAvgTicket, setEditedAvgTicket] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { goals, totalGoal, isLoading: goalsLoading, upsertGoal, isUpdating } = useUserSalesGoals(year, month, businessUnitValue);
  const { goalAmount: teamGoalAmount, avgTicket, updateGoal: updateTeamGoal, isUpdating: isUpdatingTeam } = useSalesGoals(year, month, businessUnitValue);
  const { data: salesUsers, isLoading: usersLoading } = useSalesConsultants();

  const { usGoal, brGoal, totalGoal: consolidatedGoal, isLoading: consolidatedLoading } = useConsolidatedSalesGoal(year, month);
  const isConsolidated = activeTab === 'consolidated';

  const filteredSalesUsers = useMemo(() => salesUsers ?? [], [salesUsers]);
  const userIds = useMemo(() => filteredSalesUsers.map(u => u.id), [filteredSalesUsers]);

  // Global source weights
  const { data: globalWeights } = useGlobalSourceWeights(year, month, businessUnitValue);

  // Source actuals for consultants (only on BU tabs)
  const { data: actualsMap } = useSourceActuals(
    !isConsolidated ? userIds : [],
    year, month, businessUnitValue
  );

  const goalsMap = useMemo(() => {
    const map: Record<string, number> = {};
    goals.forEach(g => { map[g.user_id] = parseFloat(String(g.goal_amount)) || 0; });
    return map;
  }, [goals]);

  const displayedTotal = useMemo(() => {
    if (!filteredSalesUsers) return totalGoal;
    return filteredSalesUsers.reduce((sum, user) => {
      const editedValue = editedGoals[user.id];
      if (editedValue !== undefined) return sum + (parseFloat(editedValue) || 0);
      return sum + (goalsMap[user.id] || 0);
    }, 0);
  }, [filteredSalesUsers, editedGoals, goalsMap, totalGoal]);

  const displayedTeamGoal = editedTeamGoal !== null ? parseFloat(editedTeamGoal) || 0 : teamGoalAmount;
  const displayedAvgTicket = editedAvgTicket !== null ? parseFloat(editedAvgTicket) || 0 : avgTicket;

  const handleGoalChange = (userId: string, value: string) => {
    setEditedGoals(prev => ({ ...prev, [userId]: value }));
    setHasChanges(true);
  };

  const handleTeamGoalChange = (value: string) => {
    setEditedTeamGoal(value);
    setHasChanges(true);
  };

  const handleAvgTicketChange = (value: string) => {
    setEditedAvgTicket(value);
    setHasChanges(true);
  };

  const handleSaveTeamGoal = () => {
    const updates: { goalAmount?: number; avgTicket?: number } = {};
    if (editedTeamGoal !== null) updates.goalAmount = parseFloat(editedTeamGoal) || 0;
    if (editedAvgTicket !== null) updates.avgTicket = parseFloat(editedAvgTicket) || 0;
    if (!Object.keys(updates).length) return;

    updateTeamGoal(updates, {
      onSuccess: () => {
        toast.success('Meta da equipe salva com sucesso');
        setEditedTeamGoal(null);
        setEditedAvgTicket(null);
        setHasChanges(Object.keys(editedGoals).length > 0);
      },
      onError: () => {
        toast.error('Erro ao salvar meta da equipe');
      },
    });
  };

  const handleSaveAll = async () => {
    const updates = Object.entries(editedGoals);

    try {
      if (editedTeamGoal !== null || editedAvgTicket !== null) {
        const teamUpdates: { goalAmount?: number; avgTicket?: number } = {};
        if (editedTeamGoal !== null) teamUpdates.goalAmount = parseFloat(editedTeamGoal) || 0;
        if (editedAvgTicket !== null) teamUpdates.avgTicket = parseFloat(editedAvgTicket) || 0;
        await new Promise((resolve, reject) => {
          updateTeamGoal(teamUpdates, { onSuccess: resolve, onError: reject });
        });
      }

      for (const [userId, value] of updates) {
        const amount = parseFloat(value) || 0;
        await new Promise((resolve, reject) => {
          upsertGoal({ userId, goalAmount: amount }, { onSuccess: resolve, onError: reject });
        });
      }

      toast.success('Todas as metas foram salvas');
      setEditedGoals({});
      setEditedTeamGoal(null);
      setEditedAvgTicket(null);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error('Erro ao salvar algumas metas');
    }
  };

  const handleSaveSingle = (userId: string) => {
    const value = editedGoals[userId];
    if (value === undefined) return;
    const amount = parseFloat(value) || 0;
    upsertGoal({ userId, goalAmount: amount }, {
      onSuccess: () => {
        toast.success('Meta salva');
        setEditedGoals(prev => {
          const newState = { ...prev };
          delete newState[userId];
          return newState;
        });
        if (Object.keys(editedGoals).length === 1 && editedTeamGoal === null && editedAvgTicket === null) {
          setHasChanges(false);
        }
      },
      onError: () => { toast.error('Erro ao salvar meta'); },
    });
  };

  const toggleExpand = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const handleTabChange = (value: string) => {
    setActiveTab(value as BusinessUnitTab);
    setEditedGoals({});
    setEditedTeamGoal(null);
    setEditedAvgTicket(null);
    setHasChanges(false);
    setExpandedUsers(new Set());
  };

  const isLoading = goalsLoading || usersLoading || (isConsolidated && consolidatedLoading);

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Target className="h-6 w-6" />
          Configuração de Metas de Vendas
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure metas mensais de vendas por Business Unit e por membro da equipe
        </p>
      </div>

      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          As metas configuradas aqui são usadas como <strong>Receita Bruta Projetada</strong> no{' '}
          <Link to="/finance/dre" className="underline font-medium hover:text-primary">
            DRE Projetado
          </Link>.
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="consolidated" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Consolidado
          </TabsTrigger>
          <TabsTrigger value="us" className="flex items-center gap-2">
            🇺🇸 USA
          </TabsTrigger>
          <TabsTrigger value="br" className="flex items-center gap-2">
            🇧🇷 Brasil
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {/* Month Navigation */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <Button variant="outline" size="icon" onClick={() => setSelectedDate(prev => subMonths(prev, 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-center">
                  <span className="text-lg font-semibold capitalize">
                    {format(selectedDate, 'MMMM yyyy', { locale: ptBR })}
                  </span>
                </div>
                <Button variant="outline" size="icon" onClick={() => setSelectedDate(prev => addMonths(prev, 1))}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Team Total Goal Card */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Meta Total da Equipe ({activeTab === 'consolidated' ? 'Consolidado' : activeTab === 'us' ? 'USA' : 'Brasil'})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isConsolidated ? (
                <div className="space-y-3">
                  {isLoading ? (
                    <Skeleton className="h-12 w-full" />
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-8 w-8 text-primary" />
                        <span className="text-3xl font-bold">{formatCurrency(consolidatedGoal)}</span>
                        <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">soma automática</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🇺🇸</span>
                          <div>
                            <p className="text-xs text-muted-foreground">USA</p>
                            <p className="font-semibold">{formatCurrency(usGoal)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🇧🇷</span>
                          <div>
                            <p className="text-xs text-muted-foreground">Brasil</p>
                            <p className="font-semibold">{formatCurrency(brGoal)}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Para editar as metas, acesse as abas <strong>USA</strong> ou <strong>Brasil</strong>.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 flex-1">
                      <DollarSign className="h-7 w-7 text-primary" />
                      {isLoading ? (
                        <Skeleton className="h-9 w-32" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">$</span>
                          <Input
                            type="number"
                            value={editedTeamGoal !== null ? editedTeamGoal : teamGoalAmount.toString()}
                            onChange={(e) => handleTeamGoalChange(e.target.value)}
                            className={`w-40 text-2xl font-bold ${editedTeamGoal !== null ? 'border-primary' : ''}`}
                            min={0}
                          />
                        </div>
                      )}
                    </div>
                    {/* Avg Ticket field */}
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground whitespace-nowrap">Ticket Médio</div>
                      {isLoading ? (
                        <Skeleton className="h-9 w-24" />
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground text-sm">$</span>
                          <Input
                            type="number"
                            value={editedAvgTicket !== null ? editedAvgTicket : avgTicket.toString()}
                            onChange={(e) => handleAvgTicketChange(e.target.value)}
                            className={`w-24 text-sm ${editedAvgTicket !== null ? 'border-primary' : ''}`}
                            min={0}
                          />
                        </div>
                      )}
                    </div>
                    {(editedTeamGoal !== null || editedAvgTicket !== null) && (
                      <Button onClick={handleSaveTeamGoal} disabled={isUpdatingTeam} size="sm">
                        <Save className="h-4 w-4 mr-2" />
                        Salvar
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Soma individual: {formatCurrency(displayedTotal)} para {format(selectedDate, 'MMMM yyyy', { locale: ptBR })}
                  </p>

                  {/* Global Source Weights Editor */}
                  <GlobalSourceWeightsEditor
                    year={year}
                    month={month}
                    businessUnit={businessUnitValue}
                    teamGoalAmount={displayedTeamGoal}
                    avgTicket={displayedAvgTicket}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Source Summary Card - only on BU tabs */}
          {!isConsolidated && (() => {
            // Build summary using global weights × team goal + actuals
            const actualsData = actualsMap || new Map();
            const weights = globalWeights || [];

            if (weights.length === 0) return null;

            // Aggregate actuals across all consultants
            const actualsBySource = new Map<string, { revenue: number; dealCount: number }>();
            actualsData.forEach((actuals) => {
              actuals.forEach((a: any) => {
                const existing = actualsBySource.get(a.source) || { revenue: 0, dealCount: 0 };
                existing.revenue += a.revenue || 0;
                existing.dealCount += a.deal_count || 0;
                actualsBySource.set(a.source, existing);
              });
            });

            const sourceRows = weights.map(w => {
              const goalAmt = (w.weight_pct / 100) * displayedTeamGoal;
              const goalDeals = displayedAvgTicket > 0 ? Math.round(goalAmt / displayedAvgTicket) : 0;
              const actual = actualsBySource.get(w.source) || { revenue: 0, dealCount: 0 };
              return { source: w.source, goalAmount: goalAmt, goalDeals, actualRevenue: actual.revenue, actualDeals: actual.dealCount };
            });

            // Also add actuals for sources not in weights
            actualsBySource.forEach((val, source) => {
              if (!weights.some(w => w.source === source)) {
                sourceRows.push({ source, goalAmount: 0, goalDeals: 0, actualRevenue: val.revenue, actualDeals: val.dealCount });
              }
            });

            const grandGoalAmt = sourceRows.reduce((s, r) => s + r.goalAmount, 0);
            const grandGoalDeals = sourceRows.reduce((s, r) => s + r.goalDeals, 0);
            const grandActualRev = sourceRows.reduce((s, r) => s + r.actualRevenue, 0);
            const grandActualDeals = sourceRows.reduce((s, r) => s + r.actualDeals, 0);

            return (
              <Card className="mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    📊 Resumo Global por Source
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Source</TableHead>
                        <TableHead className="text-xs text-right">Meta $</TableHead>
                        <TableHead className="text-xs text-center">Meta QTD</TableHead>
                        <TableHead className="text-xs text-right">Real. $</TableHead>
                        <TableHead className="text-xs text-center">R. QTD</TableHead>
                        <TableHead className="text-xs text-right">% Ating.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sourceRows.map((r) => {
                        const ach = r.goalAmount > 0 ? (r.actualRevenue / r.goalAmount) * 100 : 0;
                        return (
                          <TableRow key={r.source}>
                            <TableCell className="text-xs font-medium">{r.source}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(r.goalAmount)}</TableCell>
                            <TableCell className="text-xs text-center">{r.goalDeals}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(r.actualRevenue)}</TableCell>
                            <TableCell className="text-xs text-center">{r.actualDeals}</TableCell>
                            <TableCell className={`text-xs text-right font-medium ${ach >= 100 ? 'text-green-600' : ach >= 50 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {r.goalAmount > 0 ? `${ach.toFixed(1)}%` : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="font-bold bg-muted/50 border-t-2">
                        <TableCell className="text-xs font-bold">TOTAL</TableCell>
                        <TableCell className="text-xs text-right font-bold">{formatCurrency(grandGoalAmt)}</TableCell>
                        <TableCell className="text-xs text-center font-bold">{grandGoalDeals}</TableCell>
                        <TableCell className="text-xs text-right font-bold">{formatCurrency(grandActualRev)}</TableCell>
                        <TableCell className="text-xs text-center font-bold">{grandActualDeals}</TableCell>
                        <TableCell className="text-xs text-right font-bold">
                          {grandGoalAmt > 0 ? `${((grandActualRev / grandGoalAmt) * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })()}

          {/* Consultant Summary Card - only on BU tabs */}
          {!isConsolidated && (() => {
            const actualsData = actualsMap || new Map();
            const consultantRows = filteredSalesUsers.map(user => {
              const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
              const currentGoal = editedGoals[user.id] !== undefined ? (parseFloat(editedGoals[user.id]) || 0) : (goalsMap[user.id] ?? 0);
              const userActuals = actualsData.get(user.id) || [];
              const actualRevenue = userActuals.reduce((s: number, a: any) => s + (a.revenue || 0), 0);
              const actualDeals = userActuals.reduce((s: number, a: any) => s + (a.deal_count || 0), 0);
              const goalDeals = displayedAvgTicket > 0 ? Math.round(currentGoal / displayedAvgTicket) : 0;
              return { userName, goalAmount: currentGoal, goalDeals, actualRevenue, actualDeals };
            }).filter(r => r.goalAmount > 0 || r.actualRevenue > 0);

            const totGoal = consultantRows.reduce((s, r) => s + r.goalAmount, 0);
            const totGoalDeals = consultantRows.reduce((s, r) => s + r.goalDeals, 0);
            const totActRev = consultantRows.reduce((s, r) => s + r.actualRevenue, 0);
            const totActDeals = consultantRows.reduce((s, r) => s + r.actualDeals, 0);

            if (consultantRows.length === 0) return null;

            return (
              <Card className="mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    👥 Resumo por Consultor
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Consultor</TableHead>
                        <TableHead className="text-xs text-right">Meta $</TableHead>
                        <TableHead className="text-xs text-center">Meta QTD</TableHead>
                        <TableHead className="text-xs text-right">Real. $</TableHead>
                        <TableHead className="text-xs text-center">R. QTD</TableHead>
                        <TableHead className="text-xs text-right">% Ating.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consultantRows.map((r) => {
                        const ach = r.goalAmount > 0 ? (r.actualRevenue / r.goalAmount) * 100 : 0;
                        return (
                          <TableRow key={r.userName}>
                            <TableCell className="text-xs font-medium">{r.userName}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(r.goalAmount)}</TableCell>
                            <TableCell className="text-xs text-center">{r.goalDeals}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(r.actualRevenue)}</TableCell>
                            <TableCell className="text-xs text-center">{r.actualDeals}</TableCell>
                            <TableCell className={`text-xs text-right font-medium ${ach >= 100 ? 'text-green-600' : ach >= 50 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {r.goalAmount > 0 ? `${ach.toFixed(1)}%` : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="font-bold bg-muted/50 border-t-2">
                        <TableCell className="text-xs font-bold">TOTAL</TableCell>
                        <TableCell className="text-xs text-right font-bold">{formatCurrency(totGoal)}</TableCell>
                        <TableCell className="text-xs text-center font-bold">{totGoalDeals}</TableCell>
                        <TableCell className="text-xs text-right font-bold">{formatCurrency(totActRev)}</TableCell>
                        <TableCell className="text-xs text-center font-bold">{totActDeals}</TableCell>
                        <TableCell className="text-xs text-right font-bold">
                          {totGoal > 0 ? `${((totActRev / totGoal) * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })()}

          {/* Individual Goals Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-base font-semibold">Metas Individuais</CardTitle>
                <CardDescription>
                  {isConsolidated
                    ? 'Visualização consolidada das metas de US e BR (somente leitura)'
                    : 'Defina metas mensais para cada membro da equipe. Expanda para ver o breakdown por source.'
                  }
                </CardDescription>
              </div>
              {hasChanges && !isConsolidated && (
                <Button onClick={handleSaveAll} disabled={isUpdating || isUpdatingTeam}>
                  <Save className="h-4 w-4 mr-2" />
                  Salvar Todas
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map(i => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : !filteredSalesUsers || filteredSalesUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum membro da equipe de vendas encontrado {activeTab !== 'consolidated' && `para ${activeTab === 'us' ? 'USA' : 'Brasil'}`}
                </p>
              ) : (
                <div className="space-y-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {!isConsolidated && <TableHead className="w-[40px]"></TableHead>}
                        <TableHead>Membro</TableHead>
                        <TableHead>Cargo</TableHead>
                        {activeTab === 'consolidated' && <TableHead>BU</TableHead>}
                        <TableHead className="text-right w-[200px]">Meta Mensal (USD)</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSalesUsers.map(user => {
                        const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
                        const currentGoal = goalsMap[user.id] ?? 0;
                        const editedValue = editedGoals[user.id];
                        const displayValue = editedValue !== undefined ? editedValue : currentGoal.toString();
                        const hasLocalChange = editedValue !== undefined && editedValue !== currentGoal.toString();
                        const roleName = (user.system_role as any)?.name || user.position || '-';
                        const userBU = user.primary_business_unit;
                        const isExpanded = expandedUsers.has(user.id);
                        const userGoalForSource = editedValue !== undefined ? (parseFloat(editedValue) || 0) : currentGoal;

                        return (
                          <React.Fragment key={user.id}>
                            <TableRow className={isExpanded ? 'border-b-0' : ''}>
                              {!isConsolidated && (
                                <TableCell className="pr-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => toggleExpand(user.id)}
                                  >
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </Button>
                                </TableCell>
                              )}
                              <TableCell className="font-medium">{userName}</TableCell>
                              <TableCell className="text-muted-foreground">{roleName}</TableCell>
                              {activeTab === 'consolidated' && (
                                <TableCell>
                                  {userBU === 'usa' || userBU === 'us' ? '🇺🇸' : userBU === 'brasil' || userBU === 'br' ? '🇧🇷' : '-'}
                                </TableCell>
                              )}
                              <TableCell className="text-right">
                                {isConsolidated ? (
                                  <span className="font-medium">{formatCurrency(currentGoal)}</span>
                                ) : (
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="text-muted-foreground">$</span>
                                    <Input
                                      type="number"
                                      value={displayValue}
                                      onChange={(e) => handleGoalChange(user.id, e.target.value)}
                                      className={`w-32 text-right ${hasLocalChange ? 'border-primary' : ''}`}
                                      min={0}
                                    />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {!isConsolidated && hasLocalChange && (
                                  <Button size="sm" variant="ghost" onClick={() => handleSaveSingle(user.id)} disabled={isUpdating}>
                                    <Save className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                            {/* Source Breakdown — Read-only using global weights */}
                            {!isConsolidated && isExpanded && (
                              <TableRow>
                                <TableCell colSpan={5} className="p-0 border-b">
                                  <ConsultantSourceReadonly
                                    globalWeights={globalWeights || []}
                                    userTotalGoal={userGoalForSource}
                                    avgTicket={displayedAvgTicket}
                                    actualsMap={actualsMap || new Map()}
                                    userId={user.id}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
